import { useMemo, useState } from 'react'
import { Accordion, Button, Image } from 'react-bootstrap'
import { FaChevronDown, FaChevronUp, FaImage, FaFileAlt, FaFilePdf, FaLink } from 'react-icons/fa'
import moment from 'moment'

import { SERVER_DATA } from '../../core/constants/app'
import { downloadImage } from '../../utils/download'
import {
  downloadAllFiles,
  getDocuments,
  getPhotosVehicle,
  getScansVehicle,
  getBlueDriverScansVehicle,
} from '../../utils/invoice'
import Download from '../../assets/images/icons/download-all.svg'
import IconPlayMedia from '../../assets/images/icons/play_media.svg'

const getDocTag = (url, source) => {
  if (url) {
    try {
      const hostname = new URL(url).hostname.toLowerCase()
      if (hostname.includes('oem1stop.com')) return 'OEM1Stop'
      if (hostname.includes('alldata.com')) return 'ALLDATA'
      if (hostname.includes('repairlogic.com')) return 'RepairLogic'
    } catch (e) {
      // invalid URL
    }
  }
  if (source === 'adasThink') return 'OEM'
  return null
}

const getFileType = (url, isPhoto) => {
  if (isPhoto) return 'image'
  if (!url) return 'link'
  const lower = url.toLowerCase().split('?')[0]
  if (/\.(jpg|jpeg|png|gif|webp|heic|heif|bmp|tiff?)$/.test(lower)) return 'image'
  if (/\.(pdf|doc|docx|xls|xlsx|csv|txt|rtf)$/.test(lower)) return 'document'
  return 'link'
}

// Determines display type for items in vehicle.photos[].
// S3-hosted files default to 'image' unless they have a document extension.
// External URLs (non-S3) are classified by extension or default to 'link'.
const getPhotoFileType = (path) => {
  const isS3 = path.includes('mobiletechrxus.s3') || path.includes('mobiletechassets.s3') || path.includes(SERVER_DATA)
  if (!isS3) return getFileType(path, false)
  const lower = path.toLowerCase().split('?')[0]
  if (/\.(pdf|doc|docx|xls|xlsx|csv|txt|rtf)$/.test(lower)) return 'document'
  return 'image'
}

// Document categories that may show a provider tag (QA Round 2, Finding 16).
// The OEM1Stop / ALLDATA / RepairLogic / OEM tags identify where a piece of OEM
// documentation came from, so they only belong on the two OEM sections. They
// used to render on every card — Calibration Report, Additional Attachments,
// Pre-Scan, Post-Scan and the photo bucket included. Mirrors the admin portal.
const OEM_TAG_CATEGORIES = ['OEM Position Statement', 'OEM Procedure']

const AdasDocCard = ({ title, documents = [], onPhotoClick }) => {
  return (
    <div className="adas-doc-card">
      <div className="adas-doc-card__header">
        <span className="adas-doc-card__title">{title}</span>
      </div>
      {documents.length > 0 ? (
        <div className="adas-doc-card__files">
          {documents.map((doc, idx) => {
            // Finding 16 — provider tags only on the OEM sections.
            const tag = OEM_TAG_CATEGORIES.includes(title)
              ? doc.tag || getDocTag(doc.path, doc.source)
              : null
            return (
              <div key={idx} className="adas-doc-card__file">
                <div className="adas-doc-card__file-info">
                  {doc.fileType === 'image' ? (
                    <FaImage className="adas-doc-card__file-icon adas-doc-card__file-icon--photo" size={18} />
                  ) : doc.fileType === 'document' ? (
                    doc.path && doc.path.toLowerCase().split('?')[0].endsWith('.pdf')
                      ? <FaFilePdf className="adas-doc-card__file-icon adas-doc-card__file-icon--doc" size={18} />
                      : <FaFileAlt className="adas-doc-card__file-icon adas-doc-card__file-icon--doc" size={18} />
                  ) : (
                    <FaLink className="adas-doc-card__file-icon" size={18} />
                  )}
                  {doc.fileType === 'image' ? (
                    <button
                      type="button"
                      className="adas-doc-card__file-name adas-doc-card__file-name--clickable"
                      onClick={() => {
                        const imageList = documents.filter(d => d.fileType === 'image')
                        const clickedIndex = imageList.indexOf(doc)
                        onPhotoClick && onPhotoClick(doc, imageList, clickedIndex >= 0 ? clickedIndex : 0)
                      }}
                    >
                      {doc.fileName}
                    </button>
                  ) : (
                    <a
                      href={doc.path}
                      target="_blank"
                      rel="noreferrer"
                      className={`adas-doc-card__file-name${doc.fileType === 'link' ? ' adas-doc-card__file-name--link' : ''}`}
                    >
                      {doc.fileName}
                    </a>
                  )}
                  {tag && (
                    <span className="adas-doc-card__file-source">{tag}</span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <span className="adas-doc-card__empty">No resources to show</span>
      )}
    </div>
  )
}

// AB#39926 — the "Additional ADAS Documents" card(s) a vehicle.documents row is
// routed to, or `[]` when it is not recognised.
//
// Lifted unchanged out of getAdditionalAdasDocs so the Diagnostic Scan row in
// getAdasServiceDocs can read the same answer: that row takes exactly the
// sensor-scoped documents this names no card for. It deliberately stops short of
// the sensor-less fallback (phase → scan card, else ADAS Report), which stays in
// getAdditionalAdasDocs — a sensor-scoped document never reaches it.
//
// Duplicated from the admin portal's
// Web/admin-portal/src/components/presentations/EstimateDetail/VehiclePhotos.js:
// the two portals deploy separately, so keep the copies in step by hand.
const additionalAdasDocTargets = (doc) => {
  const nameLower = (doc.name || '').toLowerCase()
  const slot = (doc.slot || '').toLowerCase()
  // The persisted sensorId is a hyphenated slug ('diagnostic-scan'), so
  // normalise separators before any word-based test.
  const sensorLower = (doc.sensorId || '').toLowerCase().replace(/[-_]+/g, ' ')
  // "Pre and Post-Repair Scan" is a real operation name; tested first because
  // a plain substring match hits post- and never reaches pre-.
  const isCombinedScan = /pre\s*(?:and|&|\+|\/)\s*post[\s-]*(?:repair|calibration)/.test(nameLower)

  let targets = []
  if (nameLower.includes('repair estimate') || slot === 'repair-estimate' || slot === 'repair_estimate') {
    targets = ['Repair Estimate']
  } else if (nameLower.includes('adas report') || slot === 'adas-report' || slot === 'adas_report') {
    targets = ['ADAS Report']
  } else if (isCombinedScan) {
    targets = ['Pre-Calibration Scan', 'Post-Calibration Scan']
  } else if (
    nameLower.includes('post-calibration') ||
    nameLower.includes('post calibration') ||
    nameLower.includes('post-repair') ||
    nameLower.includes('post repair') ||
    slot === 'post-calibration-diagnostic-scan'
  ) {
    targets = ['Post-Calibration Scan']
  } else if (
    nameLower.includes('pre-calibration') ||
    nameLower.includes('pre calibration') ||
    nameLower.includes('pre-repair') ||
    nameLower.includes('pre repair') ||
    slot === 'pre-calibration-diagnostic-scan'
  ) {
    targets = ['Pre-Calibration Scan']
  } else if (nameLower.includes('wheel alignment') || nameLower.includes('alignment audit') || slot === 'wheel-alignment-audit') {
    targets = ['Wheel Alignment Audit']
  } else if (sensorLower.includes('diagnostic scan') && (doc.phase === 'pre' || doc.phase === 'post')) {
    // Diagnostic Scan sensor + an explicit workflow phase = a scan document.
    // The slug alone is not enough: that sensor also carries ordinary OEM
    // documentation with phase null, which does not belong in a scan card.
    targets = [doc.phase === 'post' ? 'Post-Calibration Scan' : 'Pre-Calibration Scan']
  }
  return targets
}

// AB#39926 — true for a service whose nested svc.documents and svc.photos
// getAdditionalAdasDocs routes, every one of them (a typed document to its card,
// everything else to Pre-Calibration Scan). getAdasServiceDocs skips that nested
// media on the same test; one predicate, so the two builders cannot drift apart.
//
// sensorName only, as it always was. Not the row name: that falls back to the
// sensor slug, so a service with no sensorName still gets a "Diagnostic Scan" row
// but none of its nested media routed there — the row has to keep it.
const isDiagnosticScanService = (svc) => (svc.sensorName || '').toLowerCase().includes('diagnostic scan')

const getAdasServiceDocs = (vehicle) => {
  const adas = vehicle && vehicle.adas
  if (!adas || !adas.status) return []

  const services = adas.services || []
  const groupMap = {}

  const formatSensorId = (id) => id ? id.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : ''

  for (const svc of services) {
    if (!svc) continue

    const serviceName = svc.sensorName || formatSensorId(svc.sensorId) || svc.categoryName || svc.operation?.name || 'Service'
    // Diagnostic Scan gets its own subcategory row like every other sensor, so the
    // service photos taken against it (vehicle.photos tagged sensorId
    // 'diagnostic-scan') have somewhere to render.
    //
    // This used to `continue`, on the premise in the old comment that Diagnostic Scan
    // data all routes into the Additional ADAS Document buckets. That never held for
    // its PHOTOS, which getAdditionalAdasDocs drops with `if (photo.sensorId)
    // continue`, and the standalone gallery drops with `if (p.sensorId) return false`.
    // Skipped here and rejected there, a Diagnostic Scan photo had no reachable render
    // path on any surface. Same defect shape as #37095, which fixed it for documents
    // only.
    //
    // It holds for only some of its DOCUMENTS. getAdditionalAdasDocs takes a
    // sensor-scoped vehicle.document only when additionalAdasDocTargets names a card
    // for it (the pre/post scans #37095 rescued, estimates, reports, alignment) and
    // drops the rest — adasThink OEM links, and every Admin Portal upload into this
    // row's tiles, which add-adas-photo stores as documents with sensorId
    // 'diagnostic-scan' and no phase. It does take every nested svc.documents /
    // svc.photos entry of an isDiagnosticScanService service. So this row renders
    // the complement: vehicle.documents only when additionalAdasDocTargets(doc) is
    // empty, bucketed by name like every other sensor, and no nested media when
    // isDiagnosticScanService(svc).
    //
    // Invariant: Diagnostic Scan media Additional ADAS Documents shows stays there,
    // media it drops renders here, and nothing renders in both.
    //
    // Mirrors the same fix in Web/admin-portal EstimateDetail/VehiclePhotos.js. The
    // two portals deploy separately, so this logic is duplicated rather than shared;
    // keep them in step.
    const isDiagnosticScan = serviceName.toLowerCase().includes('diagnostic scan')
    const skipNestedMedia = isDiagnosticScanService(svc)

    const groupKey = serviceName
    const photoBucket = `${serviceName} Photos`

    if (!groupMap[groupKey]) {
      groupMap[groupKey] = {
        serviceName,
        sensorId: svc.sensorId,
        categories: {
          'OEM Position Statement': [],
          'OEM Procedure': [],
          'Calibration Report': [],
          [photoBucket]: [],
          'Additional Attachments': [],
        },
        _pulledSensorIds: new Set(),
      }
    }

    const categories = groupMap[groupKey].categories
    const pulledSensorIds = groupMap[groupKey]._pulledSensorIds
    // Vehicle-level photos/documents are filtered by sensorId. When 2+ services share
    // the same sensorId in one subcategory, only iterate vehicle.photos / vehicle.documents
    // once per sensorId — otherwise each file gets pushed N times.
    const shouldPullVehicleLevel = svc.sensorId && !pulledSensorIds.has(svc.sensorId)
    if (svc.sensorId) pulledSensorIds.add(svc.sensorId)

    if (Array.isArray(svc.documents) && !skipNestedMedia) {
      for (const doc of svc.documents) {
        if (!doc) continue
        const docType = doc.type || doc.name || ''
        let category = 'Additional Attachments'

        if (docType.toLowerCase().includes('oem') && docType.toLowerCase().includes('position')) {
          category = 'OEM Position Statement'
        } else if (docType.toLowerCase().includes('oem') && docType.toLowerCase().includes('procedure')) {
          category = 'OEM Procedure'
        } else if (docType.toLowerCase().includes('photo') || docType.toLowerCase().includes('camera')) {
          category = photoBucket
        }

        if (Array.isArray(doc.files)) {
          for (const file of doc.files) {
            if (file && file.fileURL) {
              const path = file.fileURL.startsWith('http') ? file.fileURL : `${SERVER_DATA}/${file.fileURL.replace(/^\//, '')}`
              categories[category].push({
                fileName: doc.originalName || doc.name || docType || 'Document',
                path,
                source: file.source || null,
                fileType: getFileType(path, false),
              })
            }
          }
        }
      }
    }

    if (Array.isArray(svc.photos) && !skipNestedMedia) {
      for (const photo of svc.photos) {
        if (photo && photo.url) {
          const path = photo.url.startsWith('http') ? photo.url : `${SERVER_DATA}/${photo.url.replace(/^\//, '')}`
          const nameLower = (photo.name || '').toLowerCase()
          const displayName = photo.originalName || photo.name
          const fileType = getPhotoFileType(path)
          if (nameLower.includes('oem') && nameLower.includes('position')) {
            categories['OEM Position Statement'].push({ fileName: displayName, path, fileType })
          } else if (nameLower.includes('oem') && nameLower.includes('procedure')) {
            categories['OEM Procedure'].push({ fileName: displayName, path, fileType })
          } else if (nameLower.includes('calibration report')) {
            categories['Calibration Report'].push({ fileName: displayName, path, fileType })
          } else {
            categories[photoBucket].push({ fileName: displayName || 'Photo', path, fileType })
          }
        }
      }
    }

    if (svc.calibrationReport && svc.calibrationReport.url) {
      const path = svc.calibrationReport.url.startsWith('http') ? svc.calibrationReport.url : `${SERVER_DATA}/${svc.calibrationReport.url.replace(/^\//, '')}`
      categories['Calibration Report'].push({
        fileName: 'Calibration Report',
        path,
        fileType: getFileType(path, false),
      })
    }

    if (shouldPullVehicleLevel) {
      const vehiclePhotos = vehicle && vehicle.photos ? vehicle.photos : []
      for (const photo of vehiclePhotos) {
        if (!photo || !photo.url || !photo.sensorId) continue
        if (photo.sensorId !== svc.sensorId) continue
        const path = photo.url.startsWith('http') ? photo.url : `${SERVER_DATA}/${photo.url.replace(/^\//, '')}`
        const nameLower = (photo.name || '').toLowerCase()
        const displayName = photo.originalName || photo.name
        const fileType = getPhotoFileType(path)
        if (nameLower.includes('calibration report')) {
          categories['Calibration Report'].push({ fileName: displayName, path, fileType })
        } else if (nameLower.includes('oem') && nameLower.includes('position')) {
          categories['OEM Position Statement'].push({ fileName: displayName, path, fileType })
        } else if (nameLower.includes('oem') && nameLower.includes('procedure')) {
          categories['OEM Procedure'].push({ fileName: displayName, path, fileType })
        } else if (nameLower.includes('additional')) {
          // matches "Additional Attachment" and "Additional Media" (mobile app uses the latter)
          categories['Additional Attachments'].push({ fileName: displayName, path, fileType })
        } else {
          categories[photoBucket].push({ fileName: displayName || 'Photo', path, fileType })
        }
      }

      // Pull vehicle.documents that have this sensor's sensorId (PDFs, links).
      const vehicleDocs = vehicle && vehicle.documents ? vehicle.documents : []
      for (const doc of vehicleDocs) {
        if (!doc || doc.service !== 'adas' || !doc.sensorId) continue
        if (doc.sensorId !== svc.sensorId) continue
        if (!Array.isArray(doc.files)) continue
        // Diagnostic Scan: Additional ADAS Documents renders this one — see the note
        // at the top of this loop.
        if (isDiagnosticScan && additionalAdasDocTargets(doc).length) continue
        const nameLower = (doc.name || '').toLowerCase()
        let category = photoBucket
        if (nameLower.includes('calibration report')) {
          category = 'Calibration Report'
        } else if (nameLower.includes('oem') && nameLower.includes('position')) {
          category = 'OEM Position Statement'
        } else if (nameLower.includes('oem') && nameLower.includes('procedure')) {
          category = 'OEM Procedure'
        } else if (nameLower.includes('additional')) {
          // matches "Additional Attachment" and "Additional Media"
          category = 'Additional Attachments'
        }
        for (const file of doc.files) {
          if (file && file.fileURL) {
            const path = file.fileURL.startsWith('http') ? file.fileURL : `${SERVER_DATA}/${file.fileURL.replace(/^\//, '')}`
            categories[category].push({
              fileName: doc.originalName || doc.name || 'Document',
              path,
              fileType: getFileType(path, false),
            })
          }
        }
      }
    }
  }

  return Object.values(groupMap).map(({ _pulledSensorIds, ...rest }) => rest)
}

const getAdditionalAdasDocs = (vehicle) => {
  const adas = vehicle && vehicle.adas
  if (!adas) return {}

  const categories = {
    'Repair Estimate': [],
    'ADAS Report': [],
    'Pre-Calibration Scan': [],
    'Post-Calibration Scan': [],
    'Wheel Alignment Audit': [],
  }

  // NOTE: this routing is deliberately duplicated from the admin portal's
  // Web/admin-portal/src/components/presentations/EstimateDetail/VehiclePhotos.js.
  // The two portals deploy separately, so a shared module would not exist at
  // runtime — keep the two copies in step by hand (#37095).
  //
  // Identity of a document file for the top-level dedupe below: TARGET CARD +
  // row label + resolved path, never the path alone.
  //
  // label — two different documents legitimately share one fileURL, and a
  //         path-only key silently drops whichever loses the race. The label
  //         must be the SAME string the row is labelled with: keying on
  //         `doc.name` while the label fell back through `originalName || name`
  //         let two rows the user reads as identical hash differently, and both
  //         survived.
  // target — and because the label falls back to `originalName`, the UPLOADED
  //         FILENAME (API models/Vehicle/index.js), two documents built from one
  //         upload share it. Attach `scan.pdf` once as a Calibration Report and
  //         once as REPAIR ESTIMATE and a global key collapses them, emptying
  //         the Repair Estimate card — #37095 again. Scoping the key to the card
  //         keeps duplicate rows within a card collapsing while documents on
  //         DIFFERENT cards can never delete each other.
  const docLabel = (doc, fallback) =>
    (doc && (doc.originalName || doc.name)) || fallback || 'Document'
  const docKey = (target, name, path) => `${target}|${(name || '').toLowerCase()}|${path}`

  // Seeded ONLY from the nested DOCUMENT loops (adas.services[].documents and
  // adas.documentation[phase].documents) — the real double-count risk, since
  // extractNestedMedia lifts nested docs onto vehicle.documents. Never seeded
  // from the photo loops or from already-populated buckets: that makes the
  // dedupe first-wins across loops that run least-specific-first and empties
  // cards that had content.
  const nestedDocKeys = new Set()

  if (Array.isArray(adas.services)) {
    for (const svc of adas.services) {
      if (!svc) continue
      // getAdasServiceDocs skips this service's nested media on the same predicate
      const isDiagSvc = isDiagnosticScanService(svc)

      if (Array.isArray(svc.documents)) {
        for (const doc of svc.documents) {
          if (!doc || !Array.isArray(doc.files)) continue
          const docType = doc.type || ''

          let category = null
          if (docType === 'repair-estimate') category = 'Repair Estimate'
          else if (docType === 'adas-report') category = 'ADAS Report'
          else if (docType === 'pre-calibration-diagnostic-scan') category = 'Pre-Calibration Scan'
          else if (docType === 'post-calibration-diagnostic-scan') category = 'Post-Calibration Scan'
          else if (docType === 'wheel-alignment-audit') category = 'Wheel Alignment Audit'
          else if (isDiagSvc) category = 'Pre-Calibration Scan'

          if (category) {
            for (const file of doc.files) {
              if (file && file.fileURL) {
                const path = file.fileURL.startsWith('http') ? file.fileURL : `${SERVER_DATA}/${file.fileURL.replace(/^\//, '')}`
                const label = docLabel(doc, docType)
                nestedDocKeys.add(docKey(category, label, path))
                categories[category].push({
                  fileName: label,
                  path,
                  source: file.source || null,
                  fileType: getFileType(path, false),
                })
              }
            }
          }
        }
      }

      if (isDiagSvc && Array.isArray(svc.photos)) {
        for (const photo of svc.photos) {
          if (photo && photo.url) {
            const path = photo.url.startsWith('http') ? photo.url : `${SERVER_DATA}/${photo.url.replace(/^\//, '')}`
            categories['Pre-Calibration Scan'].push({
              fileName: photo.originalName || photo.name || 'Photo',
              path,
              fileType: 'image',
              _fromPhoto: true,
            })
          }
        }
      }
    }
  }

  if (adas.documentation) {
    const phaseMap = { pre: 'Pre-Calibration Scan', post: 'Post-Calibration Scan' }
    for (const [phase, bucket] of Object.entries(phaseMap)) {
      const phaseData = adas.documentation[phase]
      if (!phaseData) continue
      if (Array.isArray(phaseData.documents)) {
        for (const doc of phaseData.documents) {
          if (doc && Array.isArray(doc.files)) {
            for (const file of doc.files) {
              if (file && file.fileURL) {
                const path = file.fileURL.startsWith('http') ? file.fileURL : `${SERVER_DATA}/${file.fileURL.replace(/^\//, '')}`
                const label = docLabel(doc, doc.type)
                nestedDocKeys.add(docKey(bucket, label, path))
                categories[bucket].push({
                  fileName: label,
                  path,
                  source: file.source || null,
                  fileType: getFileType(path, false),
                })
              }
            }
          }
        }
      }
      if (Array.isArray(phaseData.photos)) {
        for (const photo of phaseData.photos) {
          if (photo && photo.url) {
            const path = photo.url.startsWith('http') ? photo.url : `${SERVER_DATA}/${photo.url.replace(/^\//, '')}`
            categories[bucket].push({
              fileName: photo.originalName || photo.name || 'Photo',
              path,
              fileType: 'image',
              _fromPhoto: true,
            })
          }
        }
      }
    }
  }

  const photos = vehicle && vehicle.photos ? vehicle.photos : []
  const tirePattern = /^(rear|front)\s*(left|right)\s*tire$/i
  const cornerPattern = /^(lt|rt)\s*(rear|front)\s*tire$/i

  for (const photo of photos) {
    if (!photo || !photo.url) continue
    if (photo.sensorId) continue
    const url = photo.url || ''
    const name = (photo.name || '').trim()
    const displayName = photo.originalName || name
    const nameLower = name.toLowerCase()
    const path = url.startsWith('http') ? url : `${SERVER_DATA}/${url.replace(/^\//, '')}`
    const fileType = getPhotoFileType(path)
    const entry = { fileName: displayName, path, fileType, _fromPhoto: true }

    if (nameLower.includes('repair estimate')) {
      categories['Repair Estimate'].push(entry)
    } else if (nameLower.includes('calibration report') || nameLower.includes('adas report')) {
      categories['ADAS Report'].push(entry)
    } else if (nameLower.includes('pre-calibration') || nameLower.includes('pre calibration')) {
      categories['Pre-Calibration Scan'].push(entry)
    } else if (nameLower.includes('post-calibration') || nameLower.includes('post calibration')) {
      categories['Post-Calibration Scan'].push(entry)
    } else if (nameLower.includes('wheel alignment') || nameLower.includes('alignment audit')) {
      categories['Wheel Alignment Audit'].push(entry)
    } else if (nameLower.includes('fuel weight compensation')) {
      categories['Wheel Alignment Audit'].push(entry)
    } else if (tirePattern.test(name) || cornerPattern.test(name)) {
      categories['Wheel Alignment Audit'].push(entry)
    }
  }

  // Scan vehicle.documents for ADAS docs.
  //
  // This used to skip anything carrying a sensorId. That assumption does not
  // survive conversion (#37095): extractNestedMedia lifts nested service media
  // onto the top-level documents array, so Pre-/Post-Repair Scans arrive here
  // carrying the Diagnostic Scans sensorId and were dropped by both loops —
  // leaving the Pre/Post-Calibration Scan cards reading "No resources to show".
  //
  // Sensor-scoped docs are considered here now, but ONLY the ones that resolve
  // to one of these five categories (additionalAdasDocTargets). Letting the rest
  // fall through to the phase/catch-all rules turns "ADAS Report" into a second
  // copy of everything getAdasServiceDocs already renders per sensor.
  //
  // Only the signals the Document sub-schema persists are matched — name, slot,
  // sensorId, phase. `doc.type` / `doc.sensorName` are not paths on that schema.
  const seenDocKeys = new Set(nestedDocKeys)

  const vehicleDocs = vehicle && vehicle.documents ? vehicle.documents : []
  for (const doc of vehicleDocs) {
    if (!doc || doc.service !== 'adas') continue
    if (!Array.isArray(doc.files)) continue
    let targets = additionalAdasDocTargets(doc)

    if (!targets.length) {
      // Sensor-scoped and unrecognised: already rendered in its sensor group —
      // Diagnostic Scan's included, which takes exactly these (AB#39926).
      if (doc.sensorId) continue
      if (doc.phase === 'pre') targets = ['Pre-Calibration Scan']
      else if (doc.phase === 'post') targets = ['Post-Calibration Scan']
      else targets = ['ADAS Report']
    }

    for (const file of doc.files) {
      if (!file || !file.fileURL) continue
      const path = file.fileURL.startsWith('http') ? file.fileURL : `${SERVER_DATA}/${file.fileURL.replace(/^\//, '')}`
      const label = docLabel(doc)
      // Per TARGET, not per file: a combined "Pre and Post-Repair Scan" belongs
      // on two cards, and a document must only be suppressed on a card that
      // already shows it.
      for (const target of targets) {
        const key = docKey(target, label, path)
        if (seenDocKeys.has(key)) continue // this card already shows it
        seenDocKeys.add(key)
        categories[target].push({
          fileName: label,
          path,
          fileType: getFileType(path, false),
        })
      }
    }
  }

  // A file that is BOTH a photo row and a document row renders twice. The photo
  // loops cannot join `seenDocKeys` — seeding the dedupe from them is what
  // emptied cards in #37095. Resolve the collision at the end instead, in the
  // direction that cannot lose content: within a single category the
  // document-sourced entry always wins and only the duplicate PHOTO entry goes.
  for (const bucket of Object.keys(categories)) {
    const documentPaths = new Set(
      categories[bucket].filter((e) => e && !e._fromPhoto).map((e) => e.path)
    )
    categories[bucket] = categories[bucket]
      .filter((e) => !(e && e._fromPhoto && documentPaths.has(e.path)))
      // eslint-disable-next-line no-unused-vars
      .map(({ _fromPhoto, ...entry }) => entry)
  }

  return categories
}

const VehiclePhotos = ({ vehicle, showScans = true }) => {
  const [activeKey, setActiveKey] = useState('0')
  const [isLoading, setIsLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [photoActive, setPhotoActive] = useState(0)
  const [mediaExpanded, setMediaExpanded] = useState(false)
  const [adasPhotoModal, setAdasPhotoModal] = useState(null)

  const fileDocuments = useMemo(() => getDocuments(vehicle), [vehicle])

  const photos = useMemo(() => {
    const adasDocNames = /repair estimate|calibration report|adas report|pre-calibration|post-calibration|wheel alignment|alignment audit|fuel weight compensation/i
    const tirePattern = /^(rear|front)\s*(left|right)\s*tire$/i
    const cornerPattern = /^(lt|rt)\s*(rear|front)\s*tire$/i
    return getPhotosVehicle(vehicle).filter((p) => {
      if (p.name && p.name.startsWith('ADAS ')) return false
      if (p.sensorId) return false
      if (p.name && adasDocNames.test(p.name)) return false
      if (p.name && tirePattern.test(p.name.trim())) return false
      if (p.name && cornerPattern.test(p.name.trim())) return false
      // Exclude external URLs that aren't actual images (links added via Add URL)
      if (getPhotoFileType(p.getURL || p.url || '') === 'link') return false
      return true
    })
  }, [vehicle])

  const scans = useMemo(() => {
    if (!showScans) return []
    const arr = getScansVehicle(vehicle) || []
    return Array.isArray(arr) ? arr.filter((item) => !!item) : []
  }, [vehicle, showScans])

  const bdScans = useMemo(() => {
    if (!showScans) return []
    const arr = getBlueDriverScansVehicle(vehicle) || []
    return Array.isArray(arr) ? arr.filter((item) => !!item) : []
  }, [vehicle, showScans])

  const adasServiceDocs = useMemo(() => getAdasServiceDocs(vehicle), [vehicle])
  const additionalAdasDocs = useMemo(() => getAdditionalAdasDocs(vehicle), [vehicle])

  const hasAdas = vehicle?.adas?.status
  const hasAttachments = photos.length > 0 || fileDocuments.length > 0 || scans.length > 0 || bdScans.length > 0
    || adasServiceDocs.some(g => Object.values(g.categories).some(docs => docs.some(d => d.fileType !== 'link')))
    || Object.values(additionalAdasDocs).some(docs => docs.some(d => d.fileType !== 'link'))

  const downloadFiles = () => {
    if (!hasAttachments) return
    setIsLoading(true)
    downloadAllFiles(vehicle, () => setIsLoading(false))
  }

  const downloadPhoto = (url, name) => {
    const link = document.createElement('a')
    link.href = url
    link.download = name
    link.click()
  }

  const displayPhotoDetail = (index) => {
    setShowModal(true)
    setPhotoActive(index)
  }

  const handleAdasPhotoClick = (doc, imageList, index) => {
    setAdasPhotoModal({
      images: imageList || [doc],
      index: index || 0,
    })
  }

  if (
    !hasAdas &&
    photos.length <= 0 &&
    fileDocuments.length <= 0 &&
    scans.length <= 0 &&
    bdScans.length <= 0
  ) return null

  return (
    <div className='py-3 position-relative' style={{ zIndex: 2 }}>
      {isLoading && (
        <div className="loading">
          <div className="loader" />
        </div>
      )}
      <Accordion
        defaultActiveKey="0"
        onSelect={setActiveKey}
        className="text-end"
      >
        <Accordion.Toggle
          as={Button}
          variant="default"
          eventKey="0"
          className="ms-auto fs-sub text-primary text-uppercase text-btn-change-status"
        >
          {activeKey === '0' ? (
            <>
              Hide Attachments
              <FaChevronUp size={14} className="ms-2" />
            </>
          ) : (
            <>
              See Attachments
              <FaChevronDown size={14} className="ms-2" />
            </>
          )}
        </Accordion.Toggle>
        <Accordion.Collapse eventKey="0">
          <div className="text-start">
            {/* Diagnostic Scans Documents */}
            {(scans.length > 0 || bdScans.length > 0) && (
              <div className="adas-section">
                <span className="adas-section__title">Diagnostic Scans Documents</span>
                <div className="adas-section__grid">
                  <AdasDocCard
                    title="Pre-Scan"
                    onPhotoClick={handleAdasPhotoClick}
                    documents={[
                      ...scans.filter(s => s.scanType?.toLowerCase().includes('pre')).map(s => ({ fileName: s.scanType && s.scanType.length > 4 ? s.scanType : 'Scan Document', path: s.path, fileType: getFileType(s.path, false) })),
                      ...bdScans.filter(s => s.fileName?.toLowerCase().includes('pre')).map(s => ({ fileName: s.fileName || 'Scan Document', path: s.path, fileType: getFileType(s.path, false) })),
                    ]}
                  />
                  <AdasDocCard
                    title="Post-Scan"
                    onPhotoClick={handleAdasPhotoClick}
                    documents={[
                      ...scans.filter(s => s.scanType?.toLowerCase().includes('post')).map(s => ({ fileName: s.scanType && s.scanType.length > 4 ? s.scanType : 'Scan Document', path: s.path, fileType: getFileType(s.path, false) })),
                      ...bdScans.filter(s => s.fileName?.toLowerCase().includes('post')).map(s => ({ fileName: s.fileName || 'Scan Document', path: s.path, fileType: getFileType(s.path, false) })),
                    ]}
                  />
                </div>
              </div>
            )}

            {/* Media Section */}
            {photos.length > 0 && (
              <div className="adas-section">
                <span className="adas-section__title">Media</span>
                <div className={`adas-media-grid ${mediaExpanded ? 'adas-media-grid--expanded' : ''}`}>
                  {photos.map((photo, index) => (
                    <div key={`${photo.url}-${index}`} className="adas-media-item">
                      <div
                        className="adas-media-item__image"
                        onClick={() => displayPhotoDetail(index)}
                      >
                        {photo.videoURL && (
                          <div className="play-media-overlay">
                            <Image src={IconPlayMedia} className="w-100" alt={photo.name} />
                          </div>
                        )}
                        <Image
                          src={photo.getURL || `${SERVER_DATA}/${photo.url}`}
                          className="w-100"
                          alt={photo.name}
                        />
                      </div>
                      <span className="adas-media-item__caption">{photo.name}</span>
                    </div>
                  ))}
                </div>
                {photos.length > 3 && (
                  <button
                    type="button"
                    className="adas-media-expand-btn"
                    onClick={() => setMediaExpanded(!mediaExpanded)}
                  >
                    {mediaExpanded ? 'Collapse images' : 'Expand all images'}
                    {mediaExpanded ? <FaChevronUp size={12} className="ms-2" /> : <FaChevronDown size={12} className="ms-2" />}
                  </button>
                )}
              </div>
            )}

            {/* ADAS Per-Service Documentation */}
            {hasAdas && (
              <div className="adas-section">
                <span className="adas-section__title">ADAS</span>
                <div className="adas-services-docs">
                  {adasServiceDocs.map((group, idx) => (
                    <div key={idx} className="adas-service-group">
                      <span className="adas-service-group__name">{group.serviceName}</span>
                      <div className="adas-section__grid">
                        {Object.entries(group.categories).map(([catName, docs]) => (
                          <AdasDocCard
                            key={catName}
                            title={catName}
                            documents={docs}
                            onPhotoClick={handleAdasPhotoClick}
                          />
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="adas-service-group">
                    <span className="adas-service-group__name">Additional ADAS Documents</span>
                    <div className="adas-section__grid">
                      {Object.entries(additionalAdasDocs).map(([catName, docs]) => (
                        <AdasDocCard key={catName} title={catName} documents={docs} onPhotoClick={handleAdasPhotoClick} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="text-center py-3">
              <button
                type="button"
                className="fakeButton d-inline-flex align-items-center"
                style={{
                  fontSize: '12px',
                  letterSpacing: '0.5px',
                  color: hasAttachments ? '#307FE2' : '#a0aec0',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  cursor: hasAttachments ? 'pointer' : 'default',
                  opacity: hasAttachments ? 1 : 0.6,
                  gap: '8px',
                }}
                onClick={hasAttachments ? downloadFiles : undefined}
              >
                DOWNLOAD ALL ATTACHMENTS
                <img src={Download} style={{ width: 24, height: 24 }} alt="" />
              </button>
            </div>
          </div>
        </Accordion.Collapse>
      </Accordion>

      {/* Photo detail modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center',
            alignItems: 'center', zIndex: 1000, padding: '10px', overflowY: 'scroll',
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff', borderRadius: '8px', width: '100%', maxWidth: '600px',
              maxHeight: '90vh', display: 'flex', flexDirection: 'column', position: 'relative',
              padding: '1.5rem', marginTop: '2rem',
            }}
          >
            <button
              type="button"
              onClick={() => setShowModal(false)}
              style={{
                position: 'absolute', right: '1rem', top: '1rem', cursor: 'pointer',
                background: 'none', border: 'none', fontSize: '20px', color: '#333',
              }}
              aria-label="Close"
            >
              ✕
            </button>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <img
                src={photos[photoActive].getURL || `${SERVER_DATA}/${photos[photoActive].url}`}
                alt={photos[photoActive].name}
                style={{ maxWidth: '100%', maxHeight: '50vh', objectFit: 'contain' }}
              />
            </div>

            {photos[photoActive].note && (
              <div style={{ textAlign: 'center', marginBottom: '1rem', fontSize: '0.9rem' }}>
                {photos[photoActive].note}
              </div>
            )}
            <div style={{ textAlign: 'center' }}>
              <span>
                <span style={{ fontWeight: '600' }}>Verified Photo:</span>
                {` ${photos[photoActive].date
                  ? `${moment(photos[photoActive].date).format('MM.DD.YY')} at ${moment(photos[photoActive].date).format('hh:mma')}`
                  : ''}`}
              </span>
            </div>

            <div className="row justify-content-center mt-4 align-items-center">
              <button
                className="fakeButton d-flex align-items-center"
                style={{ textTransform: 'uppercase', color: '#307FE2', fontWeight: 'bold', textAlign: 'center' }}
                type="button"
                onClick={() => downloadPhoto(
                  photos[photoActive].getURL || `${SERVER_DATA}/${photos[photoActive].url}`,
                  photos[photoActive].name
                )}
              >
                Download Photo
                <img style={{ margin: '0 0 0.15rem 1rem' }} src={Download} alt="" />
              </button>
            </div>
            <div className="row justify-content-center mt-4 align-items-center">
              <button
                onClick={() => setPhotoActive((prev) => prev === 0 ? photos.length - 1 : prev - 1)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', marginRight: '20px' }}
              >
                <i className="fa fa-chevron-left" />
              </button>
              <button
                onClick={() => setPhotoActive((prev) => prev === photos.length - 1 ? 0 : prev + 1)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', marginLeft: '20px' }}
              >
                <i className="fa fa-chevron-right" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADAS photo modal */}
      {adasPhotoModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center',
            alignItems: 'center', zIndex: 1000, padding: '10px', overflowY: 'scroll',
          }}
          onClick={() => setAdasPhotoModal(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff', borderRadius: '8px', width: '100%', maxWidth: '600px',
              maxHeight: '90vh', display: 'flex', flexDirection: 'column', position: 'relative',
              padding: '1.5rem', marginTop: '2rem',
            }}
          >
            <button
              type="button"
              onClick={() => setAdasPhotoModal(null)}
              style={{
                position: 'absolute', right: '1rem', top: '1rem', cursor: 'pointer',
                background: 'none', border: 'none', fontSize: '20px', color: '#333',
              }}
              aria-label="Close"
            >
              ✕
            </button>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <img
                src={adasPhotoModal.images[adasPhotoModal.index].path}
                alt={adasPhotoModal.images[adasPhotoModal.index].fileName}
                style={{ maxWidth: '100%', maxHeight: '50vh', objectFit: 'contain' }}
              />
            </div>
            {adasPhotoModal.images[adasPhotoModal.index].note && (
              <div style={{ textAlign: 'center', marginBottom: '1rem', fontSize: '0.9rem' }}>
                {adasPhotoModal.images[adasPhotoModal.index].note}
              </div>
            )}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem',
                marginTop: '1.5rem',
              }}
            >
              <button
                className="fakeButton"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  textTransform: 'uppercase',
                  color: '#307FE2',
                  fontWeight: 'bold',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
                type="button"
                onClick={() => {
                  const current = adasPhotoModal.images[adasPhotoModal.index]
                  downloadImage(current.path, current.fileName)
                }}
              >
                Download Photo
                <img style={{ marginLeft: '0.5rem' }} src={Download} alt="" />
              </button>
              {adasPhotoModal.images.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <button
                    onClick={() => setAdasPhotoModal(prev => ({ ...prev, index: prev.index === 0 ? prev.images.length - 1 : prev.index - 1 }))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '4px 8px', color: '#307FE2' }}
                    aria-label="Previous"
                  >
                    <i className="fa fa-chevron-left" />
                  </button>
                  <span style={{ fontSize: '13px', color: '#69787F', minWidth: '50px', textAlign: 'center' }}>
                    {adasPhotoModal.index + 1} / {adasPhotoModal.images.length}
                  </span>
                  <button
                    onClick={() => setAdasPhotoModal(prev => ({ ...prev, index: prev.index === prev.images.length - 1 ? 0 : prev.index + 1 }))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '4px 8px', color: '#307FE2' }}
                    aria-label="Next"
                  >
                    <i className="fa fa-chevron-right" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VehiclePhotos

// Exported for __tests__/adasAdditionalDocs.test.js. This routing is duplicated
// from the admin portal by design (separate deploys), which makes the copies
// free to drift — and the two production bugs it has caused (#37095, and the
// Repair Estimate rendering twice) were both dedupe/ordering interactions
// between the four sources it merges. The admin copy is pinned by an identical
// suite; this one has to be pinned too or "keep them in step by hand" is a hope.
// getAdasServiceDocs and additionalAdasDocTargets are pinned by
// __tests__/adasDiagnosticScan.test.js (AB#39926) for the same reason.
export { getAdditionalAdasDocs, getAdasServiceDocs, additionalAdasDocTargets }
